import { useState, useEffect, useContext } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import HeaderBackground from '../components/headerbackground.js';
import getAPI from '../functions/API/getAPI.js';
import postAPI from '../functions/API/postAPI.js';
import { toast } from '../lib/toast.js';
import GlobalContext from '../context/GlobalContext.js';
import {
    Wrench, CheckCircle2, Hammer, Plus, FolderOpen, Phone, Mail,
    MessageCircle, Sun, CloudSun, Car, Calendar, ClipboardList,
    AlertTriangle, ChevronRight, FileText, Clock, X, Lock,
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const TIME_SLOTS = [
    { id: 'Anytime',          label: 'Anytime',          description: "I'm flexible with timing",    Icon: Calendar },
    { id: 'Morning',          label: 'Morning',           description: 'Before 12pm',                  Icon: Sun },
    { id: 'Afternoon',        label: 'Afternoon',         description: 'After 12pm',                   Icon: CloudSun },
    { id: 'Avoid school run', label: 'Avoid school run', description: 'Not between 8–9am or 3–4pm',  Icon: Car },
];

function contactIcon(cd) {
    const d = (cd.description || '').toLowerCase();
    const r = (cd.reference || '').toLowerCase();
    if (d.includes('mobile') || d.includes('phone') || r.includes('mobile')) return Phone;
    if (d.includes('email') || r.includes('@')) return Mail;
    return MessageCircle;
}

function extractTitle(description) {
    const m = description?.match(/^\[([^\]]+)\]:/);
    return m ? m[1] : description;
}

// Strips the "[Definition]: " prefix and trailing "[Preferred time slot: …]" / "[Access note: …]" tags
function extractBody(description) {
    if (!description || !/^\[[^\]]+\]:/.test(description)) return '';
    return description
        .replace(/^\[[^\]]+\]:\s*/, '')
        .replace(/\s*\[(Preferr?ed time slot|Access note):[^\]]*\]/gi, '')
        .trim();
}

function emptyForm() {
    return {
        category: null, categoryName: '',
        definition: null, definitionName: '',
        contactDetail: null, contactDetailName: '',
        timeSlot: '',
        description: '',
        accessNote: '',
    };
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Repairs() {
    const { theme, themeCSS, settings } = useContext(GlobalContext);
    const { agreementAddress } = useOutletContext() || {};
    const primaryColor = settings?.primary_hex_colour || '#813468';

    // ── Data ──────────────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [repairs, setRepairs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [repairDefinitions, setRepairDefinitions] = useState([]);
    const [definitionsLoading, setDefinitionsLoading] = useState(false);
    const [contactDetails, setContactDetails] = useState([]);
    const [agreement, setAgreement] = useState(null);
    const [assetId, setAssetId] = useState(null);

    // ── View & filter ─────────────────────────────────────────────────────────
    // list | form | category | type | contact | timeslot | details | review
    const [view, setView] = useState('list');
    const [filterTab, setFilterTab] = useState('open');

    // ── Form ──────────────────────────────────────────────────────────────────
    const [formData, setFormData] = useState(emptyForm());

    // ── Modals ────────────────────────────────────────────────────────────────
    const [emergencyOpen, setEmergencyOpen] = useState(false);
    const [safetyNotice, setSafetyNotice] = useState(null);
    const [safetyOpen, setSafetyOpen] = useState(false);
    const [cancelId, setCancelId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [selectedRepair, setSelectedRepair] = useState(null);

    // ── API ───────────────────────────────────────────────────────────────────
    async function loadData() {
        setLoading(true);
        const ref = localStorage.getItem('agreement');
        if (!ref) { setLoading(false); return; }

        const agRes = await getAPI('agreements/' + ref);
        if (!agRes.response?.ok) { setLoading(false); return; }
        const agr = agRes.data;
        setAgreement(agr);
        setAssetId(agr.assetid);

        const [repairsRes, contactRes] = await Promise.all([
            getAPI('repairs/' + agr.assetid),
            getAPI('contactdetail'),
        ]);

        if (repairsRes.response?.ok) {
            const raw = Array.isArray(repairsRes.data) ? repairsRes.data : [];
            // Deduplicate by id and normalise iscomplete to a boolean
            const seen = new Set();
            const deduped = [];
            for (const r of raw) {
                const key = r.id || r.repairid;
                if (key && seen.has(key)) continue;
                if (key) seen.add(key);
                deduped.push({ ...r, iscomplete: r.iscomplete === true || r.iscomplete === 1 || r.iscomplete === '1' || r.iscomplete === 'true' });
            }
            setRepairs(deduped);
        }
        if (contactRes.response?.ok) setContactDetails(Array.isArray(contactRes.data) ? contactRes.data : []);
        setLoading(false);
    }

    async function loadCategories() {
        if (categories.length > 0) return; // already loaded
        setCategoriesLoading(true);
        const catRes = await getAPI('category?active=1');
        if (!catRes.response?.ok) { setCategoriesLoading(false); return; }
        const cats = catRes.data || [];
        const filtered = [];
        await Promise.all(cats.map(async (cat) => {
            const defRes = await getAPI(`repairdefinitions?categoryid=${cat.id}&agreementreference=${agreement.reference}`);
            if (Array.isArray(defRes.data) && defRes.data.length > 0) filtered.push(cat);
        }));
        setCategories(filtered);
        setCategoriesLoading(false);
    }

    async function loadDefinitions(categoryId) {
        setDefinitionsLoading(true);
        const res = await getAPI(`repairdefinitions?categoryid=${categoryId}&agreementreference=${agreement.reference}`);
        if (res.response?.ok) setRepairDefinitions(Array.isArray(res.data) ? res.data : []);
        setDefinitionsLoading(false);
    }

    async function cancelRepair() {
        const id = cancelId;
        setCancelId(null);
        setLoading(true);
        const res = await postAPI('cancelrepair', { repairid: id });
        if (res.response?.ok) {
            toast.success('Repair cancelled successfully');
            loadData();
        } else {
            toast.error('Something went wrong, please try again');
            setLoading(false);
        }
    }

    async function submitRepair() {
        setSubmitting(true);
        let desc = `[${formData.definitionName}]: ${formData.description} [Preferred time slot: ${formData.timeSlot}]`;
        if (formData.accessNote) desc += ` [Access note: ${formData.accessNote}]`;
        const body = {
            assetid: assetId,
            description: desc,
            definitionid: formData.definition,
            communicationid: formData.contactDetail,
            accessnote: formData.accessNote || '',
            agreementreference: agreement.reference,
        };
        const res = await postAPI('repairs', body);
        if (res.response?.ok) {
            toast.success('Repair requested successfully');
            setFormData(emptyForm());
            setCategories([]); // reset so they reload fresh next time
            setView('list');
            loadData();
        } else {
            toast.error('Something went wrong, please try again');
        }
        setSubmitting(false);
    }

    const location = useLocation();
    useEffect(() => {
        setView('list');
        loadData();
    }, [location.key]);

    // ── Navigation ────────────────────────────────────────────────────────────
    const canRequest = agreement && agreement.type !== 'Expired';

    function openForm() {
        setEmergencyOpen(true);
    }

    function proceedToForm() {
        setEmergencyOpen(false);
        setFormData(emptyForm());
        setView('form');
        loadCategories();
    }

    function resetFlow() {
        setFormData(emptyForm());
        setView('list');
    }

    function openCategory() { setView('category'); }

    function openType() {
        if (!formData.category) { toast.error('Please select a category first'); return; }
        loadDefinitions(formData.category);
        setView('type');
    }

    function selectDefinition(def) {
        setFormData(prev => ({ ...prev, definition: def.id, definitionName: def.description }));
        if (def.safety_notice) {
            setSafetyNotice(def.safety_notice);
            setSafetyOpen(true);
        } else {
            setView('form');
        }
    }

    function confirmSafety() {
        setSafetyOpen(false);
        setView('form');
    }

    function cancelSafety() {
        setSafetyOpen(false);
        setFormData(prev => ({ ...prev, definition: null, definitionName: '' }));
    }

    function openContact() { setView('contact'); }

    function openTimeSlot() { setView('timeslot'); }

    function openDetails() { setView('details'); }

    const formComplete =
        formData.category &&
        formData.definition &&
        formData.contactDetail &&
        formData.timeSlot &&
        formData.description.trim();

    // ── Page title / back / right action ─────────────────────────────────────
    const pageConfig = {
        list:     { title: 'Repairs',         onBack: null,        right: canRequest ? PlusButton() : null },
        form:     { title: 'New Repair',       onBack: resetFlow,   right: null },
        category: { title: 'Select Category',  onBack: () => setView('form'), right: null },
        type:     { title: 'Select Repair',    onBack: () => setView('form'), right: null },
        contact:  { title: 'Contact Method',   onBack: () => setView('form'), right: null },
        timeslot: { title: 'Preferred Time',   onBack: () => setView('form'), right: null },
        details:  { title: 'Details',          onBack: () => setView('form'), right: null },
        review:   { title: 'Review & Submit',  onBack: () => setView('form'), right: null },
    };
    const { title: pageTitle, onBack, right: rightAction } = pageConfig[view] || pageConfig.list;

    function PlusButton() {
        return (
            <button
                onClick={openForm}
                className="w-9 h-9 rounded-full flex items-center justify-center text-white bg-white/20 hover:bg-white/30 transition-colors"
                aria-label="New repair"
            >
                <Plus size={20} strokeWidth={2.5} />
            </button>
        );
    }

    // ── Shared styles ─────────────────────────────────────────────────────────
    function SelectionRow({ selected, onClick, Icon, title, subtitle, locked }) {
        return (
            <button
                onClick={locked ? undefined : onClick}
                disabled={locked}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-colors shadow-sm ${
                    locked ? 'opacity-60 cursor-not-allowed' : themeCSS.cardHover
                } ${themeCSS.cardBg}`}
            >
                <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: selected ? `${primaryColor}22` : (theme === 'dark' ? '#1e293b' : '#F3F4F6') }}
                >
                    <Icon size={20} strokeWidth={1.5} style={{ color: selected ? primaryColor : (theme === 'dark' ? '#94A3B8' : '#6B7280') }} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>{title}</p>
                    <p className={`text-xs mt-0.5 truncate ${selected ? themeCSS.cardSubtext : themeCSS.cardMuted}`}>{subtitle}</p>
                </div>
                {locked ? (
                    <Lock size={16} className={`flex-shrink-0 ${themeCSS.cardMuted}`} strokeWidth={1.5} />
                ) : selected ? (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-green-500">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                ) : (
                    <ChevronRight size={18} className={`flex-shrink-0 ${themeCSS.cardMuted}`} strokeWidth={1.5} />
                )}
            </button>
        );
    }

    function PickerRow({ selected, onClick, Icon, label, description }) {
        return (
            <button
                onClick={onClick}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-colors shadow-sm ${themeCSS.cardBg} ${themeCSS.cardHover}`}
                style={selected ? { outline: `2px solid ${primaryColor}`, outlineOffset: '-2px', backgroundColor: `${primaryColor}0d` } : {}}
            >
                <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: selected ? `${primaryColor}22` : (theme === 'dark' ? '#1e293b' : '#F3F4F6') }}
                >
                    <Icon size={20} strokeWidth={1.5} style={{ color: selected ? primaryColor : (theme === 'dark' ? '#94A3B8' : '#6B7280') }} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                    <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>{label}</p>
                    {description && <p className={`text-xs mt-0.5 ${themeCSS.cardMuted}`}>{description}</p>}
                </div>
                {selected && (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                )}
            </button>
        );
    }

    // ── Skeleton ──────────────────────────────────────────────────────────────
    function SkeletonList() {
        return (
            <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className={`${themeCSS.cardBg} rounded-xl px-4 py-3.5 flex items-center gap-3`}>
                        <div className={`w-10 h-10 rounded-xl ${themeCSS.skeleton} animate-pulse flex-shrink-0`} />
                        <div className="flex-1 space-y-2">
                            <div className={`h-4 rounded-md ${themeCSS.skeleton} animate-pulse w-2/3`} />
                            <div className={`h-3 rounded ${themeCSS.skeleton} animate-pulse w-1/3`} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // ── LIST VIEW ─────────────────────────────────────────────────────────────
    function ListView() {
        const filtered = repairs.filter(r =>
            filterTab === 'open' ? !r.iscomplete : r.iscomplete
        );

        return (
            <div className="space-y-3">
                {canRequest && (
                    <div className="hidden lg:flex justify-end">
                        <button
                            onClick={openForm}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                            style={{ backgroundColor: primaryColor }}
                        >
                            <Plus size={16} /> New Repair
                        </button>
                    </div>
                )}
                <div className={`flex items-center rounded-full p-1 ${themeCSS.cardBg} shadow-sm`}>
                    {['open', 'closed'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setFilterTab(tab)}
                            className={`flex-1 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                filterTab === tab ? 'text-white shadow-sm' : themeCSS.cardMuted
                            }`}
                            style={filterTab === tab ? { backgroundColor: primaryColor } : undefined}
                        >
                            {tab === 'open' ? 'Open' : 'Completed'}
                        </button>
                    ))}
                </div>

                {filtered.length === 0 ? (
                    <div
                        className={`${themeCSS.cardBg} rounded-2xl p-8 flex flex-col items-center text-center`}
                        style={{ border: `2px dashed ${theme === 'dark' ? '#334155' : '#F2F2F7'}` }}
                    >
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
                            <Hammer size={28} className="text-gray-300" strokeWidth={1.5} />
                        </div>
                        <p className={`text-base font-semibold mb-2 ${themeCSS.cardSubtext}`}>
                            No {filterTab === 'open' ? 'open' : 'completed'} repairs
                        </p>
                        <p className={`text-sm ${themeCSS.cardMuted}`}>
                            {filterTab === 'open' ? 'All your repairs are up to date' : 'No completed repairs to show'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((repair) => {
                            const isComplete = repair.iscomplete;
                            const Icon = isComplete ? CheckCircle2 : Wrench;
                            const title = extractTitle(repair.description);
                            return (
                                <div key={repair.repairid} onClick={() => setSelectedRepair(repair)} className={`${themeCSS.cardBg} rounded-xl px-4 py-3.5 flex items-center gap-3 shadow-sm cursor-pointer ${themeCSS.cardHover} transition-colors`}>
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: isComplete ? '#16A34A22' : '#0EA5E922' }}
                                    >
                                        <Icon size={20} strokeWidth={1.5} style={{ color: isComplete ? '#16A34A' : '#0EA5E9' }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium leading-snug truncate ${themeCSS.cardTitle}`}>{title}</p>
                                        <p className={`text-xs mt-0.5 ${themeCSS.cardMuted}`}>#{repair.repairid} &middot; {repair.date}</p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span
                                            className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                                            style={isComplete
                                                ? { backgroundColor: '#DCFCE7', color: '#16A34A' }
                                                : { backgroundColor: '#FEF3C7', color: '#D97706' }
                                            }
                                        >
                                            {repair.status}
                                        </span>
                                        <ChevronRight size={16} className={themeCSS.cardMuted} strokeWidth={1.5} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // ── FORM OVERVIEW (hub) ───────────────────────────────────────────────────
    function FormView() {
        const sections = [
            {
                key: 'category',
                Icon: FolderOpen,
                title: 'Category',
                hint: 'What area is the repair in?',
                value: formData.categoryName,
                locked: false,
                onPress: openCategory,
            },
            {
                key: 'type',
                Icon: Wrench,
                title: 'Repair type',
                hint: formData.category ? 'What needs fixing?' : "Complete 'Category' first",
                value: formData.definitionName,
                locked: !formData.category,
                onPress: openType,
            },
            {
                key: 'contact',
                Icon: Phone,
                title: 'Contact method',
                hint: 'How should we contact you?',
                value: formData.contactDetailName,
                locked: false,
                onPress: openContact,
            },
            {
                key: 'timeslot',
                Icon: Clock,
                title: 'Preferred time',
                hint: 'When suits you best?',
                value: formData.timeSlot,
                locked: false,
                onPress: openTimeSlot,
            },
            {
                key: 'details',
                Icon: FileText,
                title: 'Details',
                hint: 'Describe the issue',
                value: formData.description ? formData.description.slice(0, 60) + (formData.description.length > 60 ? '…' : '') : '',
                locked: false,
                onPress: openDetails,
            },
        ];

        return (
            <div className="space-y-4">
                {/* Property address */}
                {agreementAddress && (
                    <div className={`flex items-center gap-2 px-1`}>
                        <span className={`text-sm ${themeCSS.cardMuted}`}>{agreementAddress}</span>
                    </div>
                )}

                {/* Emergency warning */}
                <div className={`${themeCSS.cardBg} rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm`}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-red-100">
                        <AlertTriangle size={18} strokeWidth={2} className="text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-700">Not for emergencies</p>
                        <p className={`text-xs ${themeCSS.cardMuted}`}>
                            {settings?.repair_emergency_number
                                ? `For emergencies, call ${settings.repair_emergency_number}`
                                : 'For emergencies, please call us directly'}
                        </p>
                    </div>
                </div>

                {/* Instruction */}
                <p className={`text-sm px-1 ${themeCSS.cardSubtext}`}>
                    Complete each section below to request your repair.
                </p>

                {/* Section cards */}
                <div className="space-y-2">
                    {sections.map(s => (
                        SelectionRow({
                            key: s.key,
                            selected: !!s.value,
                            onClick: s.onPress,
                            Icon: s.Icon,
                            title: s.title,
                            subtitle: s.value || s.hint,
                            locked: s.locked,
                        })
                    ))}
                </div>

                {/* Submit */}
                <button
                    onClick={() => setView('review')}
                    disabled={!formComplete}
                    className="w-full py-3.5 rounded-xl text-sm font-semibold transition-opacity disabled:opacity-40"
                    style={formComplete ? { backgroundColor: primaryColor, color: '#fff' } : { backgroundColor: theme === 'dark' ? '#334155' : '#E5E7EB', color: theme === 'dark' ? '#94A3B8' : '#9CA3AF' }}
                >
                    Complete all sections
                </button>
            </div>
        );
    }

    // ── CATEGORY VIEW ─────────────────────────────────────────────────────────
    function CategoryView() {
        if (categoriesLoading) {
            return (
                <div className="space-y-2">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className={`${themeCSS.cardBg} rounded-xl px-4 py-3.5 flex items-center gap-3`}>
                            <div className={`w-10 h-10 rounded-xl ${themeCSS.skeleton} animate-pulse flex-shrink-0`} />
                            <div className={`h-4 rounded-md ${themeCSS.skeleton} animate-pulse flex-1`} />
                        </div>
                    ))}
                </div>
            );
        }
        if (categories.length === 0) {
            return (
                <div className={`${themeCSS.cardBg} rounded-2xl p-8 text-center`}>
                    <p className="text-red-500 font-semibold text-sm">No repair options currently available.</p>
                </div>
            );
        }
        return (
            <div className="space-y-3">
                <p className={`text-sm ${themeCSS.cardMuted} px-1`}>Select a repair category</p>
                <div className="space-y-2">
                    {categories.map(cat => (
                        PickerRow({
                            key: cat.id,
                            selected: formData.category === cat.id,
                            onClick: () => {
                                setFormData(prev => ({
                                    ...prev,
                                    category: cat.id,
                                    categoryName: cat.friendlyname,
                                    definition: null,
                                    definitionName: '',
                                }));
                                setView('form');
                            },
                            Icon: FolderOpen,
                            label: cat.friendlyname,
                        })
                    ))}
                </div>
            </div>
        );
    }

    // ── TYPE VIEW ─────────────────────────────────────────────────────────────
    function TypeView() {
        if (definitionsLoading) {
            return (
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className={`${themeCSS.cardBg} rounded-xl px-4 py-3.5 flex items-center gap-3`}>
                            <div className={`w-10 h-10 rounded-xl ${themeCSS.skeleton} animate-pulse flex-shrink-0`} />
                            <div className={`h-4 rounded-md ${themeCSS.skeleton} animate-pulse flex-1`} />
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div className="space-y-3">
                <p className={`text-sm ${themeCSS.cardMuted} px-1`}>Select the type of repair needed</p>
                <div className="space-y-2">
                    {repairDefinitions.map(def => (
                        PickerRow({
                            key: def.id,
                            selected: formData.definition === def.id,
                            onClick: () => selectDefinition(def),
                            Icon: Wrench,
                            label: def.description,
                            description: def.safety_notice ? 'Safety notice applies' : undefined,
                        })
                    ))}
                </div>
            </div>
        );
    }

    // ── CONTACT VIEW ──────────────────────────────────────────────────────────
    function ContactView() {
        return (
            <div className="space-y-3">
                <p className={`text-sm ${themeCSS.cardMuted} px-1`}>Best contact method for this repair</p>
                <div className="space-y-2">
                    {contactDetails.map(cd => (
                        PickerRow({
                            key: cd.referenceid,
                            selected: formData.contactDetail === cd.referenceid,
                            onClick: () => {
                                setFormData(prev => ({
                                    ...prev,
                                    contactDetail: cd.referenceid,
                                    contactDetailName: `${cd.description} — ${cd.reference}`,
                                }));
                                setView('form');
                            },
                            Icon: contactIcon(cd),
                            label: cd.reference,
                            description: cd.description,
                        })
                    ))}
                </div>
            </div>
        );
    }

    // ── TIME SLOT VIEW ────────────────────────────────────────────────────────
    function TimeSlotView() {
        return (
            <div className="space-y-3">
                <p className={`text-sm ${themeCSS.cardMuted} px-1`}>Preferred time — we can't guarantee availability</p>
                <div className="space-y-2">
                    {TIME_SLOTS.map(slot => (
                        PickerRow({
                            key: slot.id,
                            selected: formData.timeSlot === slot.id,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, timeSlot: slot.id }));
                                setView('form');
                            },
                            Icon: slot.Icon,
                            label: slot.label,
                            description: slot.description,
                        })
                    ))}
                </div>
            </div>
        );
    }

    // ── DETAILS VIEW ──────────────────────────────────────────────────────────
    function DetailsView() {
        const inputBase = `w-full rounded-xl px-4 py-3 text-sm border-0 ring-0 outline-none focus:outline-none focus:ring-0 resize-none ${
            theme === 'dark'
                ? 'bg-slate-600 text-white placeholder-gray-400'
                : theme === 'highContrast'
                ? 'bg-white text-black placeholder-gray-600 border-2 border-black'
                : 'bg-white text-gray-900 placeholder-gray-400'
        }`;

        return (
            <div className="space-y-3">
                <div className="rounded-xl px-4 py-3 flex gap-3" style={{ backgroundColor: '#FFFBEB' }}>
                    <FileText size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#F59E0B' }} strokeWidth={1.5} />
                    <p className="text-xs" style={{ color: '#92400E' }}>
                        Provide as much detail as possible to help us resolve your repair quickly.
                    </p>
                </div>
                <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                    <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>
                        Description *
                    </label>
                    <textarea
                        rows={6}
                        className={inputBase}
                        placeholder="Please describe the repair in as much detail as possible…"
                        value={formData.description}
                        onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    />
                </div>
                <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                    <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>
                        Access Note (optional)
                    </label>
                    <input
                        type="text"
                        className={`${inputBase} py-3`}
                        placeholder="E.g. Please use the side door"
                        value={formData.accessNote}
                        onChange={e => setFormData(prev => ({ ...prev, accessNote: e.target.value }))}
                    />
                </div>
                <button
                    onClick={() => { if (formData.description.trim()) setView('form'); else toast.error('Please provide a description'); }}
                    disabled={!formData.description.trim()}
                    className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-2 transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: primaryColor }}
                >
                    Save
                </button>
            </div>
        );
    }

    // ── REVIEW VIEW ───────────────────────────────────────────────────────────
    function ReviewView() {
        const items = [
            { label: 'Category',       value: formData.categoryName,       Icon: FolderOpen },
            { label: 'Repair type',    value: formData.definitionName,     Icon: Wrench },
            { label: 'Contact method', value: formData.contactDetailName,  Icon: Phone },
            { label: 'Preferred time', value: formData.timeSlot,           Icon: Clock },
            { label: 'Description',    value: formData.description,        Icon: ClipboardList },
            ...(formData.accessNote ? [{ label: 'Access note', value: formData.accessNote, Icon: FileText }] : []),
        ];
        return (
            <div className="space-y-3">
                <p className={`text-sm ${themeCSS.cardMuted} px-1`}>Please review your repair request</p>
                <div className={`${themeCSS.cardBg} rounded-2xl overflow-hidden shadow-sm divide-y ${themeCSS.cardBorder}`}>
                    {items.map(({ label, value, Icon }) => (
                        <div key={label} className="px-4 py-3.5 flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${primaryColor}14` }}>
                                <Icon size={16} strokeWidth={1.5} style={{ color: primaryColor }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-xs font-semibold uppercase tracking-wider mb-0.5 ${themeCSS.cardMuted}`}>{label}</p>
                                <p className={`text-sm ${themeCSS.cardTitle}`}>{value}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    onClick={submitRepair}
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-2 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ backgroundColor: primaryColor }}
                >
                    {submitting
                        ? <div className="w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                        : <><CheckCircle2 size={18} strokeWidth={2} /> Submit repair request</>
                    }
                </button>
            </div>
        );
    }

    // ── REPAIR DETAIL SHEET ──────────────────────────────────────────────────
    function RepairDetailSheet() {
        const repair = selectedRepair;
        if (!repair) return null;

        const isComplete = repair.iscomplete;
        const title = extractTitle(repair.description);
        const body = extractBody(repair.description);

        // Progress steps
        const statusSteps = ['Request', 'In Progress', 'Complete'];
        let currentStep = 0;
        const statusLower = (repair.status || '').toLowerCase();
        if (isComplete || statusLower === 'complete' || statusLower === 'completed') currentStep = 2;
        else if (statusLower === 'in progress' || statusLower === 'inprogress') currentStep = 1;
        else currentStep = 0;

        const progressMessages = [
            "We've received your request",
            "We're working on your request",
            'Repair complete',
        ];

        // Extract time from description if present
        const timeMatch = repair.description?.match(/\[Preferr?ed time slot:\s*([^\]]+)\]/i);
        const timeSlot = timeMatch ? timeMatch[1] : null;

        const sheetContent = (
            <>
                {/* Info row */}
                <div className={`flex items-stretch rounded-xl overflow-hidden ${themeCSS.cardBg} shadow-sm`}>
                    <div className={`flex-1 px-4 py-3 ${themeCSS.cardBorder} border-r`}>
                        <p className={`text-[11px] font-medium uppercase tracking-wider ${themeCSS.cardMuted}`}>Repair ID</p>
                        <p className={`text-sm font-bold mt-0.5 ${themeCSS.cardTitle}`}>#{repair.repairid}</p>
                    </div>
                    <div className={`flex-1 px-4 py-3 ${themeCSS.cardBorder} border-r`}>
                        <p className={`text-[11px] font-medium uppercase tracking-wider ${themeCSS.cardMuted}`}>Date</p>
                        <p className={`text-sm font-bold mt-0.5 ${themeCSS.cardTitle}`}>{repair.date}</p>
                    </div>
                    <div className="flex-1 px-4 py-3">
                        <p className={`text-[11px] font-medium uppercase tracking-wider ${themeCSS.cardMuted}`}>Status</p>
                        <p className={`text-sm font-bold mt-0.5 ${themeCSS.cardTitle}`}>{repair.status}</p>
                    </div>
                </div>

                {/* Progress tracker */}
                <div className={`mt-3 rounded-xl ${themeCSS.cardBg} shadow-sm px-5 py-5`}>
                    <p className={`text-sm font-bold text-center mb-4 ${themeCSS.cardTitle}`}>
                        {isComplete ? 'Repair complete' : 'Repair in progress'}
                    </p>

                    {/* Progress bar */}
                    <div className="flex items-center gap-0 px-2">
                        {statusSteps.map((_, i) => (
                            <div key={i} className="flex items-center flex-1 last:flex-none">
                                {i === currentStep ? (
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        <Clock size={18} className="text-white" strokeWidth={2} />
                                    </div>
                                ) : (
                                    <div
                                        className={`w-3 h-3 rounded-full flex-shrink-0 ${
                                            i < currentStep ? '' : (theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300')
                                        }`}
                                        style={i < currentStep ? { backgroundColor: primaryColor } : undefined}
                                    />
                                )}
                                {i < statusSteps.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-1 ${
                                        i < currentStep
                                            ? ''
                                            : (theme === 'dark' ? 'bg-slate-600' : 'bg-gray-200')
                                    }`}
                                        style={i < currentStep ? { backgroundColor: primaryColor } : undefined}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <p className={`text-sm text-center mt-4 ${themeCSS.cardMuted}`} style={currentStep < 2 ? { color: primaryColor } : undefined}>
                        {progressMessages[currentStep]}
                    </p>
                </div>

                {/* Description */}
                <div className={`mt-3 rounded-xl ${themeCSS.cardBg} shadow-sm px-5 py-4`}>
                    <p className={`text-sm font-bold ${themeCSS.cardTitle}`}>{title}</p>
                    {body && (
                        <p className={`text-sm mt-2 whitespace-pre-wrap break-words ${themeCSS.cardSubtext}`}>{body}</p>
                    )}
                    {timeSlot && (
                        <>
                            <div className={`my-3 border-t ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`} />
                            <div className="flex items-center gap-8">
                                <p className={`text-sm ${themeCSS.cardMuted}`}>Time</p>
                                <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>{timeSlot}</p>
                            </div>
                        </>
                    )}
                </div>

                {/* Cancel button */}
                {repair.can_cancel && (
                    <div className="mt-4">
                        <button
                            onClick={() => {
                                setSelectedRepair(null);
                                setCancelId(repair.repairid);
                            }}
                            className="w-full py-3 rounded-xl text-sm font-semibold text-red-500 transition-colors hover:bg-red-50"
                        >
                            Cancel Repair
                        </button>
                    </div>
                )}
            </>
        );

        return (
            <>
                {/* Backdrop */}
                <div
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn"
                    onClick={() => setSelectedRepair(null)}
                />

                {/* Mobile: bottom sheet */}
                <div className={`fixed inset-x-0 bottom-0 z-50 animate-slideUp rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto lg:hidden ${
                    theme === 'dark' ? 'bg-slate-800' : theme === 'highContrast' ? 'bg-white' : 'bg-gray-50'
                }`}>
                    <div className="flex justify-center pt-3 pb-1">
                        <div className={`w-10 h-1 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                    </div>
                    <div className="flex items-center justify-between px-5 pb-3">
                        <h2 className={`text-lg font-bold ${themeCSS.cardTitle}`}>Repair Details</h2>
                        <button onClick={() => setSelectedRepair(null)} className="text-sm font-semibold" style={{ color: primaryColor }}>Done</button>
                    </div>
                    <div className="px-5 pb-8">
                        {sheetContent}
                    </div>
                </div>

                {/* Desktop: centered modal */}
                <div className="hidden lg:flex fixed inset-0 z-50 items-center justify-center p-4">
                    <div className={`w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-slideUp ${
                        theme === 'dark' ? 'bg-slate-800' : theme === 'highContrast' ? 'bg-white' : 'bg-gray-50'
                    }`}>
                        <div className="flex items-center justify-between px-5 pt-5 pb-3">
                            <h2 className={`text-lg font-bold ${themeCSS.cardTitle}`}>Repair Details</h2>
                            <button
                                onClick={() => setSelectedRepair(null)}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`}
                            >
                                <X size={18} className={themeCSS.cardMuted} strokeWidth={2} />
                            </button>
                        </div>
                        <div className="px-5 pb-6 max-h-[70vh] overflow-y-auto">
                            {sheetContent}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ── EMERGENCY MODAL ────────────────────────────────────────────────────────
    function EmergencyModal() {
        return (
            <>
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={() => setEmergencyOpen(false)} />
                <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-center lg:p-4">
                    <div className={`w-full animate-slideUp flex flex-col rounded-t-2xl lg:rounded-2xl shadow-2xl lg:max-w-md overflow-hidden ${
                        theme === 'dark' ? 'bg-slate-800' : 'bg-white'
                    }`}>
                        {/* Drag handle — mobile only */}
                        <div className="flex justify-center pt-3 pb-1 flex-shrink-0 lg:hidden">
                            <div className={`w-10 h-1 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                        </div>

                        <div className="px-6 pt-4 lg:pt-6 pb-6">
                            {/* Icon & Title */}
                            <div className="flex flex-col items-center text-center mb-5">
                                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                                    <AlertTriangle size={32} className="text-red-600" />
                                </div>
                                <h2 className="text-xl font-bold text-red-600">Is this an emergency?</h2>
                            </div>

                            {/* Info card */}
                            <div className={`rounded-2xl p-4 border mb-5 ${theme === 'dark' ? 'bg-red-950/30 border-red-900/50' : 'bg-red-50 border-red-200'}`}>
                                <p className={`text-sm text-center leading-relaxed ${theme === 'dark' ? 'text-red-300' : 'text-red-800'}`}>
                                    If there is a health or safety risk that endangers you or your property, please call us immediately.
                                </p>
                                {settings?.repair_help_text && (
                                    <p className={`text-sm text-center leading-relaxed mt-3 ${theme === 'dark' ? 'text-red-300' : 'text-red-800'}`}>
                                        {settings.repair_help_text}
                                    </p>
                                )}
                            </div>

                            {/* Emergency phone button */}
                            {settings?.repair_emergency_number && (
                                <a
                                    href={`tel:${settings.repair_emergency_number}`}
                                    className={`flex items-center gap-3 rounded-xl px-4 py-3 border mb-5 transition-colors ${
                                        theme === 'dark' ? 'bg-red-950/30 border-red-900/50 hover:bg-red-950/50' : 'bg-red-50 border-red-200 hover:bg-red-100'
                                    }`}
                                >
                                    <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                                        <Phone size={16} className="text-white" />
                                    </div>
                                    <span className="text-base font-semibold text-red-600 flex-1">{settings.repair_emergency_number}</span>
                                    <ChevronRight size={18} className="text-red-400" />
                                </a>
                            )}

                            {/* Buttons */}
                            <div className="space-y-2.5">
                                <button
                                    onClick={proceedToForm}
                                    className="w-full py-3.5 rounded-xl text-sm font-semibold text-white"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    This is not an emergency
                                </button>
                                <button
                                    onClick={() => setEmergencyOpen(false)}
                                    className={`w-full py-3.5 rounded-xl text-sm font-semibold ${
                                        theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                                    }`}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ── SAFETY MODAL ──────────────────────────────────────────────────────────
    function SafetyModal() {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl">
                    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center gap-2">
                        <AlertTriangle size={20} className="text-amber-600" strokeWidth={2} />
                        <span className="font-semibold text-amber-800">Safety Notice</span>
                    </div>
                    <div className="px-4 py-4">
                        <p className="text-sm text-gray-700">{safetyNotice}</p>
                    </div>
                    <div className="px-4 pb-4 flex gap-2">
                        <button onClick={cancelSafety} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-600">
                            Cancel
                        </button>
                        <button onClick={confirmSafety} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: primaryColor }}>
                            I Understand
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── CANCEL CONFIRM MODAL ──────────────────────────────────────────────────
    function CancelModal() {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl">
                    <div className="px-4 py-5 text-center">
                        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                            <X size={24} className="text-red-500" strokeWidth={2} />
                        </div>
                        <p className="font-semibold text-gray-900 mb-1">Cancel repair #{cancelId}?</p>
                        <p className="text-sm text-gray-500">This action cannot be undone.</p>
                    </div>
                    <div className="px-4 pb-4 flex gap-2">
                        <button onClick={() => setCancelId(null)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-600">
                            Keep repair
                        </button>
                        <button onClick={cancelRepair} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-600 text-white">
                            Cancel repair
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen" style={{ backgroundColor: themeCSS.pageBg }}>
            <HeaderBackground pageTitle={pageTitle} onBack={onBack} rightAction={rightAction} />

            {selectedRepair && RepairDetailSheet()}
            {emergencyOpen && EmergencyModal()}
            {safetyOpen && SafetyModal()}
            {cancelId && CancelModal()}

            <div className="max-w-2xl mx-auto px-4 py-4 pb-28">
                {loading
                    ? SkeletonList()
                    : <>
                        {view === 'list'     && ListView()}
                        {view === 'form'     && FormView()}
                        {view === 'category' && CategoryView()}
                        {view === 'type'     && TypeView()}
                        {view === 'contact'  && ContactView()}
                        {view === 'timeslot' && TimeSlotView()}
                        {view === 'details'  && DetailsView()}
                        {view === 'review'   && ReviewView()}
                    </>
                }
            </div>
        </div>
    );
}
